import { useState } from "react";
import { Button, Flex, ModalV2, SelectableCards, Text, TextInput } from "@jsq/ds/core";
import type { SelectableCardOption } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";

type ApprovalChoice = "manual" | "auto";

const APPROVAL_OPTIONS: SelectableCardOption[] = [
  {
    title: "Request client approval",
    details: "Client must approve template before it's available for all staff users",
    icon: "user-check",
    value: "manual",
  },
  {
    title: "Skip client approval",
    details: "Publish immediately to all staff users",
    icon: "user-slash",
    value: "auto",
  },
];

type ConfirmPublishModalProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: (shouldAutoApprove: boolean) => void;
  arenaName: string;
  isSaving: boolean;
};

export const ConfirmPublishModal = ({
  show,
  onClose,
  onConfirm,
  arenaName,
  isSaving,
}: ConfirmPublishModalProps) => {
  const [approvalChoice, setApprovalChoice] = useState<ApprovalChoice>("manual");
  const [verificationInput, setVerificationInput] = useState("");

  const isVerified = verificationInput === arenaName;
  const showError = verificationInput.length > 0 && !isVerified;

  return (
    <ModalV2
      show={show}
      onHide={onClose}
      title="Publishing settings"
      closeOnClickOutside={false}
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Text variant="strong-md">Approval workflow</Text>
            <SelectableCards
              value={approvalChoice}
              options={APPROVAL_OPTIONS}
              onSelectOption={(value) =>
                setApprovalChoice(value === "auto" ? "auto" : "manual")
              }
            />
          </Flex>

          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Text variant="strong-md">Client workspace verification</Text>
            <TextInput
              label={
                <Text variant="secondary-md">
                  Type &quot;{arenaName}&quot; to confirm the client workspace
                </Text>
              }
              value={verificationInput}
              onSetValue={setVerificationInput}
              errorMessage={showError ? "Client name must match exactly" : undefined}
              isFullWidth
            />
          </Flex>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button variant="neutral" onClick={onClose}>
            Go back
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(approvalChoice === "auto")}
            disabled={!isVerified || isSaving}
          >
            {`Publish to ${arenaName}`}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
